import { motion } from 'framer-motion'
import { ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import type { CatalogEntry, CatalogGroup } from '@/services/api/registers'
import { cn } from '@/utils/cn'
import { CountryTag } from './CountryTag'

const GROUP_ORDER: CatalogGroup[] = ['snapshot', 'official', 'screening', 'web']

const container = { hidden: {}, show: { transition: { staggerChildren: 0.04 } } }
const item = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] as const } },
}

/** Card grid of every register / source the OSINT agent can reach, grouped by kind. */
export function RegisterCatalog({
  entries,
  className,
}: {
  entries: CatalogEntry[]
  className?: string
}) {
  const { t } = useTranslation()
  const groups = GROUP_ORDER.map(group => ({
    group,
    items: entries.filter(e => e.group === group),
  })).filter(g => g.items.length > 0)

  return (
    <div className={cn('space-y-8', className)}>
      {groups.map(({ group, items }) => (
        <section key={group}>
          <header className="mb-3 flex items-baseline gap-3 max-md:flex-wrap max-md:gap-y-0.5">
            <h3 className="text-sm font-semibold text-foreground">
              {t(`extensions.osint.catalog.groups.${group}.title`)}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t(`extensions.osint.catalog.groups.${group}.hint`)}
            </p>
          </header>
          <motion.ul
            variants={container}
            initial="hidden"
            animate="show"
            className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
          >
            {items.map(entry => (
              <motion.li
                key={entry.key}
                variants={item}
                className="group relative flex flex-col gap-2 rounded-lg border border-border bg-card p-4 transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-2">
                    <CountryTag country={entry.country} className="mt-0.5" />
                    <h4 className="text-sm font-semibold leading-snug text-foreground">
                      {entry.name}
                    </h4>
                  </div>
                  {entry.url && (
                    <a
                      href={entry.url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={t('extensions.osint.catalog.open', { name: entry.name })}
                      className="shrink-0 rounded-sm p-1 text-muted-foreground opacity-60 transition-opacity hover:text-primary group-hover:opacity-100 max-md:-m-2 max-md:p-3"
                    >
                      <ExternalLink className="size-3.5" />
                    </a>
                  )}
                </div>
                <p className="text-xs leading-relaxed text-muted-foreground">{entry.what}</p>
                {entry.sources.length > 0 && (
                  <ul className="flex flex-wrap gap-1">
                    {entry.sources.map(source => (
                      <li key={source}>
                        <Badge
                          variant="muted"
                          className="h-auto whitespace-normal py-0.5 text-2xs font-normal"
                        >
                          {source}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-auto flex items-center justify-between gap-2 pt-1 text-2xs max-md:flex-wrap max-md:gap-y-0.5">
                  <span className="font-mono text-primary-strong max-md:break-all">
                    {entry.tool}
                  </span>
                  <span className="text-muted-foreground">{entry.access}</span>
                </div>
              </motion.li>
            ))}
          </motion.ul>
        </section>
      ))}
    </div>
  )
}
